import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { ConfirmDeleteModal, LoadState, PlainText } from "./AdminShared";

const emptyAlbum = {
    title: "",
    description: "",
    default_photographer: "",
    default_description: "",
    is_public: false,
};

const AlbumManager = () => {
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [notice, setNotice] = useState("");
    const [search, setSearch] = useState("");
    const [visibility, setVisibility] = useState("all");
    const [showModal, setShowModal] = useState(false);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [albumData, setAlbumData] = useState(emptyAlbum);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const token = localStorage.getItem("token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        const loadAlbums = async () => {
            setLoading(true);
            setLoadError("");
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/albums/`, auth);
                setAlbums(response.data.results || response.data || []);
            } catch {
                setLoadError("Alben konnten nicht geladen werden. Bitte versuche es erneut.");
            } finally {
                setLoading(false);
            }
        };
        loadAlbums();
    }, []);

    const filteredAlbums = useMemo(() => {
        const query = search.trim().toLocaleLowerCase("de");
        return [...albums]
            .filter((album) => {
                const matchesSearch = !query
                    || album.title?.toLocaleLowerCase("de").includes(query)
                    || album.description?.toLocaleLowerCase("de").includes(query);
                const matchesVisibility = visibility === "all"
                    || (visibility === "public" && album.is_public)
                    || (visibility === "private" && !album.is_public);
                return matchesSearch && matchesVisibility;
            })
            .sort((a, b) => a.title.localeCompare(b.title, "de"));
    }, [albums, search, visibility]);

    const openModal = (album = null) => {
        setSelectedAlbum(album);
        setAlbumData(album ? {
            title: album.title || "",
            description: album.description || "",
            default_photographer: album.default_photographer || "",
            default_description: album.default_description || "",
            is_public: Boolean(album.is_public),
        } : emptyAlbum);
        setSaveError("");
        setShowModal(true);
    };

    const closeModal = () => {
        if (!saving) setShowModal(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setSaveError("");
        try {
            const response = selectedAlbum
                ? await axios.put(`${process.env.REACT_APP_API_URL}/albums/${selectedAlbum.id}/`, albumData, auth)
                : await axios.post(`${process.env.REACT_APP_API_URL}/albums/`, albumData, auth);
            const saved = response.data;
            setAlbums((current) => selectedAlbum
                ? current.map((album) => album.id === selectedAlbum.id ? saved : album)
                : [...current, saved]);
            setShowModal(false);
            setNotice(selectedAlbum ? "Album wurde aktualisiert." : "Album wurde erstellt.");
        } catch {
            setSaveError("Das Album konnte nicht gespeichert werden. Bitte prüfe die Eingaben.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/albums/${deleteTarget.id}/`, auth);
            setAlbums((current) => current.filter((album) => album.id !== deleteTarget.id));
            setDeleteTarget(null);
            setNotice("Album wurde gelöscht.");
        } catch {
            setNotice("");
            setLoadError("Das Album konnte nicht gelöscht werden. Möglicherweise ist es noch mit Inhalten verknüpft.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section aria-labelledby="album-admin-title">
            <div className="admin-page-heading">
                <div>
                    <p className="admin-eyebrow">Galerie</p>
                    <h1 id="album-admin-title">Alben</h1>
                    <p>Alben anlegen, beschreiben und die zugehörigen Bilder verwalten.</p>
                </div>
                <Button onClick={() => openModal()}>Neues Album</Button>
            </div>

            {notice && <Alert variant="success" dismissible onClose={() => setNotice("")}>{notice}</Alert>}
            <div className="admin-toolbar">
                <Form.Control
                    type="search"
                    aria-label="Alben durchsuchen"
                    placeholder="Titel und Beschreibung durchsuchen …"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
                <Form.Select
                    aria-label="Alben nach Sichtbarkeit filtern"
                    value={visibility}
                    onChange={(event) => setVisibility(event.target.value)}
                >
                    <option value="all">Alle Sichtbarkeiten</option>
                    <option value="public">Öffentlich</option>
                    <option value="private">Privat</option>
                </Form.Select>
            </div>

            <LoadState
                loading={loading}
                error={loadError}
                empty={filteredAlbums.length === 0}
                emptyText={search || visibility !== "all" ? "Keine Alben passen zu den Filtern." : "Noch keine Alben vorhanden."}
            >
                <div className="admin-list">
                    {filteredAlbums.map((album) => (
                        <article className="admin-item" key={album.id}>
                            <div className="admin-item__header">
                                <div>
                                    <h2 className="admin-item__title">{album.title}</h2>
                                    <div className="admin-item__meta">
                                        <span>Standard-Fotograf: {album.default_photographer || "Nicht angegeben"}</span>
                                    </div>
                                </div>
                                <span className={`admin-status${album.is_public ? " is-positive" : ""}`}>
                                    {album.is_public ? "Öffentlich" : "Privat"}
                                </span>
                            </div>
                            {album.description
                                ? <PlainText className="admin-item__description">{album.description}</PlainText>
                                : <p className="admin-item__description text-muted">Keine Beschreibung vorhanden.</p>}
                            <div className="admin-item__footer">
                                <span className="admin-item__meta">Album #{album.id}</span>
                                <div className="admin-actions">
                                    <Button as={Link} to={`/admin/albums/${album.id}/images`} variant="outline-secondary">
                                        Bilder verwalten
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => openModal(album)}>Bearbeiten</Button>
                                    <Button variant="outline-danger" onClick={() => setDeleteTarget(album)}>Löschen</Button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </LoadState>

            <Modal
                show={showModal}
                onHide={closeModal}
                centered size="lg"
                className="admin-editor-modal"
                backdropClassName="admin-modal-backdrop"
            >
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>{selectedAlbum ? "Album bearbeiten" : "Album erstellen"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {saveError && <Alert variant="danger">{saveError}</Alert>}
                    <Form id="album-editor-form" onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="album-title">
                            <Form.Label>Titel</Form.Label>
                            <Form.Control
                                value={albumData.title}
                                onChange={(event) => setAlbumData({ ...albumData, title: event.target.value })}
                                required autoFocus disabled={saving}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="album-description">
                            <Form.Label>Beschreibung</Form.Label>
                            <Form.Control
                                as="textarea" rows={7}
                                value={albumData.description}
                                onChange={(event) => setAlbumData({ ...albumData, description: event.target.value })}
                                disabled={saving}
                            />
                            <Form.Text>Zeilenumbrüche und leere Absätze bleiben in der Galerie erhalten.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="album-photographer">
                            <Form.Label>Standard-Fotograf</Form.Label>
                            <Form.Control
                                value={albumData.default_photographer}
                                onChange={(event) => setAlbumData({ ...albumData, default_photographer: event.target.value })}
                                disabled={saving}
                            />
                            <Form.Text>Wird bei neuen Bildern als Vorgabe verwendet.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="album-default-description">
                            <Form.Label>Standard-Bildbeschreibung</Form.Label>
                            <Form.Control
                                as="textarea" rows={4}
                                value={albumData.default_description}
                                onChange={(event) => setAlbumData({ ...albumData, default_description: event.target.value })}
                                disabled={saving}
                            />
                        </Form.Group>
                        <Form.Check
                            id="album-public"
                            type="switch"
                            label="Album öffentlich anzeigen"
                            checked={albumData.is_public}
                            onChange={(event) => setAlbumData({ ...albumData, is_public: event.target.checked })}
                            disabled={saving}
                        />
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={closeModal} disabled={saving}>Abbrechen</Button>
                    <Button type="submit" form="album-editor-form" disabled={saving}>
                        {saving && <Spinner animation="border" size="sm" className="me-2" />}
                        Speichern
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmDeleteModal
                show={Boolean(deleteTarget)}
                title={deleteTarget?.title || ""}
                busy={deleting}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            />
        </section>
    );
};

export default AlbumManager;
